import { makeDiscriminatedUnionPredicate } from '@sgme/fp';
import { ActionButtonWidth } from 'components/common-form/sidePanel/actions/shared/types';
import type { AnalyticCategoryActions } from 'models';
import type { ReactNode } from 'react';
import type { MapStateToPropsFactory } from 'react-redux';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { BookingActionName, QuoteActionName } from 'state/common-form/infos/infos.model';
import {
  bookableActionNames,
  execOnlyActionNames,
  executableActionNames,
  type QuoteAction,
  tradableActionNames,
} from 'state/common-form/infos/infos.model';
import { dynamicBookingRequestedThunk } from 'state/dynamic/dynamicForm.thunks';
import type { BookingType, QuoteType } from 'state/quotes/infos/infos.model';
import { quoteRequestedByWorkspaceThunk } from 'state/quotes/quotes.thunks';
import { getFormInfos, isFormInputData, isPendingEnrichment } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

export interface OwnProps {
  tabId: string;
  workspaceType: QuoteWorkspaceType;

  children?: ReactNode;

  width?: ActionButtonWidth;

  btnClassName?: string;
}

export interface PairedQuoteAction {
  action: QuoteAction;
  advancedAction?: QuoteAction;
}

export interface StateProps {
  fullWidthPairedActions: readonly PairedQuoteAction[];

  halfWidthPairedActions: readonly PairedQuoteAction[];
  allPairedActions: readonly PairedQuoteAction[];

  isFormDirty: boolean;
}

export interface DispatchProps {
  onPrice(actionName: QuoteActionName | BookingActionName): void;
}

const isBasicAction = makeDiscriminatedUnionPredicate('name', [
  'TradableQuote',
  'ExecOnly',
  'IndicativeQuote',
  'ExecutableQuote',
  'BookWithoutExecution',
  'BookWithExecution',
]);

const toFullWidthPairedActions = (actions: readonly QuoteAction[]) =>
  actions
    .filter(a => !isTradableAndExecutableAction(a))
    .filter(isBasicAction)
    .map(a => toPairedAction(a, actions));

const toHalfWidthPairedActions = (actions: readonly QuoteAction[]) =>
  actions
    .filter(isTradableAndExecutableAction)
    .filter(isBasicAction)
    .map(a => toPairedAction(a, actions));

const toAllPairedActions = (actions: readonly QuoteAction[]) =>
  actions.filter(isBasicAction).map(a => toPairedAction(a, actions));

const hasExecOnly = (actions: readonly QuoteAction[]) => actions.some(a => a.name === 'ExecOnly');
export type QuoteActionsProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps: MapStateToPropsFactory<StateProps, OwnProps, AppState> = () => {
  const getFullWidthPairedActions = createSelector(
    [
      (state: AppState, tabId: string, workspaceType: QuoteWorkspaceType) =>
        getFormInfos(state, workspaceType, tabId)!.actions,
    ],
    actions => (hasExecOnly(actions) ? toFullWidthPairedActions(actions) : []),
  );
  const getHalfWidthPairedActions = createSelector(
    [
      (state: AppState, tabId: string, workspaceType: QuoteWorkspaceType) =>
        getFormInfos(state, workspaceType, tabId)!.actions,
    ],
    actions => (hasExecOnly(actions) ? toHalfWidthPairedActions(actions) : toAllPairedActions(actions)),
  );

  const getAllPairedActions = createSelector(
    [
      (state: AppState, tabId: string, workspaceType: QuoteWorkspaceType) =>
        getFormInfos(state, workspaceType, tabId)!.actions,
    ],
    actions => toAllPairedActions(actions),
  );
  return (state, { tabId, workspaceType }) => {
    const isFormDirty = isPendingEnrichment(state, tabId) || isFormInputData(state, tabId);

    const fullWidthPairedActions = getFullWidthPairedActions(state, tabId, workspaceType);
    const halfWidthPairedActions = getHalfWidthPairedActions(state, tabId, workspaceType);
    const allPairedActions = getAllPairedActions(state, tabId, workspaceType);
    return {
      fullWidthPairedActions,
      halfWidthPairedActions,
      allPairedActions,
      isFormDirty,
    };
  };
};

const bookingTypeToTracking: Record<
  BookingType,
  AnalyticCategoryActions['Primary market static' | 'Primary market dynamic']
> = {
  BookWithExecution: 'Request for book with execution',
  BookWithoutExecution: 'Request for book without execution',
};

const quoteActionNameToQuoteType: Record<Exclude<QuoteActionName, 'ScheduleRun'>, QuoteType> = {
  IndicativeQuote: 'Indicative',
  AdvancedIndicativeQuote: 'AdvancedIndicative',
  TradableQuote: 'Tradable',
  AdvancedTradableQuote: 'AdvancedTradable',
  ExecOnly: 'ExecOnly',
  AdvancedExecOnly: 'AdvancedExecOnly',
  ExecutableQuote: 'Executable',
  AdvancedExecutableQuote: 'AdvancedExecutable',
  BookWithoutExecution: 'BookWithoutExecution',
  BookWithExecution: 'BookWithExecution',
};

const bookingActionNameToBookingType: Record<BookingActionName, BookingType> = {
  BookWithExecution: 'BookWithExecution',
  BookWithoutExecution: 'BookWithoutExecution',
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { tabId, workspaceType }: OwnProps): DispatchProps => {
  return {
    onPrice(actionName) {
      if (actionName === 'BookWithExecution' || actionName === 'BookWithoutExecution') {
        const bookingType = bookingActionNameToBookingType[actionName];
        logAnalytics.trackEvent('Primary market dynamic', bookingTypeToTracking[bookingType]);
        dispatch(dynamicBookingRequestedThunk(tabId, bookingType));
        return;
      }

      if (actionName !== 'ScheduleRun') {
        const quoteType = quoteActionNameToQuoteType[actionName];
        dispatch(quoteRequestedByWorkspaceThunk(tabId, quoteType, workspaceType));
      }
    },
  };
};

export const isTradableAndExecutableAction = makeDiscriminatedUnionPredicate('name', [
  ...execOnlyActionNames,
  ...tradableActionNames,
  ...executableActionNames,
  ...bookableActionNames,
]);

export function toPairedAction(action: QuoteAction, actions: readonly QuoteAction[]): PairedQuoteAction {
  if (!isBasicAction(action)) {
    return {
      action,
      advancedAction: undefined,
    };
  }
  const advancedActionName = `Advanced${action.name}`;
  const advancedAction = actions.find(a => a.name === advancedActionName);

  return {
    action,
    advancedAction,
  };
}

export const connectQuoteAction = connect(mapStateToProps, mapDispatchToProps);
